import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { PropsWithChildren, ReactElement } from 'react';

interface ExpandedRowsContextType {
    /** Whether there are any expanded rows */
    hasExpandedRows: boolean;
    /** Array of currently expanded rows */
    expandedRows: Array<number | string>;
    /** Function to toggle a specific row */
    toggleRow: (rowIdentifier: number | string) => void;
    /** Function to toggle all rows */
    toggleAllRows: () => void;
    /** Function to expand all rows */
    expandAllRows: () => void;
    /** Function to collapse all rows */
    collapseAllRows: () => void;
}

interface ExpandedRowsProviderProps {
    /** Array of all available rows that can be expanded */
    allAvailableRows: Array<number | string>;
    /** Initial expanded rows when the provider is rendered */
    initialExpandedRows?: Array<number | string>;
    /** Whether to automatically expand new rows when they are added, if all current rows are already expanded */
    disableAutoExpandNewRows?: boolean;
}

const ExpandedRowsContext = createContext<ExpandedRowsContextType | null>(null);

const useExpandedRows = (): ExpandedRowsContextType => {
    const context = useContext(ExpandedRowsContext);
    if (!context)
        throw new Error(
            'useExpandedRows must be used within ExpandedRowsProvider'
        );
    return context;
};

const ExpandedRowsProvider = ({
    children,
    allAvailableRows,
    initialExpandedRows = [],
    disableAutoExpandNewRows = false,
}: PropsWithChildren<ExpandedRowsProviderProps>): ReactElement => {
    const [expandedRows, setExpandedRows] =
        useState<ExpandedRowsContextType['expandedRows']>(initialExpandedRows);

    // Keep track of previous initialExpandedRows for simple comparison
    const prevInitialExpandedRowsRef =
        useRef<Array<number | string>>(initialExpandedRows);

    // Keep track of previous allAvailableRows to detect new rows
    const prevAllAvailableRowsRef =
        useRef<Array<number | string>>(allAvailableRows);

    // Will toggle a row expand state
    const toggleRow = (rowIdentifier: number | string): void => {
        setExpandedRows((prev) =>
            prev.includes(rowIdentifier)
                ? // If the row is already expanded, remove it from the list
                  prev.filter((row) => row !== rowIdentifier)
                : // Else add it to the list
                  [...prev, rowIdentifier]
        );
    };

    // Toggle all rows expand state at once
    const toggleAllRows = useCallback((): void => {
        if (expandedRows.length === 0) {
            setExpandedRows(allAvailableRows);
        } else {
            setExpandedRows([]);
        }
    }, [allAvailableRows, expandedRows.length]);

    // Expand all rows
    const expandAllRows = useCallback((): void => {
        setExpandedRows(allAvailableRows);
    }, [allAvailableRows]);

    // Collapse all rows
    const collapseAllRows = useCallback((): void => {
        setExpandedRows([]);
    }, []);

    const contextValue = useMemo(
        () => ({
            hasExpandedRows: expandedRows.length > 0,
            expandedRows,
            toggleRow,
            toggleAllRows,
            expandAllRows,
            collapseAllRows,
        }),
        [expandedRows, toggleAllRows, expandAllRows, collapseAllRows]
    );

    // Set initial expanded rows when allAvailableRows becomes available
    useEffect(() => {
        // Check if initialExpandedRows has actually changed (simple comparison)
        const initialExpandedRowsChanged =
            initialExpandedRows.length !==
                prevInitialExpandedRowsRef.current.length ||
            initialExpandedRows[0] !== prevInitialExpandedRowsRef.current[0];

        if (
            allAvailableRows.length > 0 &&
            initialExpandedRows.length > 0 &&
            expandedRows.length === 0 &&
            initialExpandedRowsChanged
        ) {
            // Filter to only include rows that actually exist in allAvailableRows
            const validInitialRows = initialExpandedRows.filter((row) =>
                allAvailableRows.includes(row)
            );

            if (validInitialRows.length > 0) {
                setExpandedRows(validInitialRows);
            }
        }

        // Update ref when initialExpandedRows changes
        if (initialExpandedRowsChanged) {
            prevInitialExpandedRowsRef.current = initialExpandedRows;
        }
        // We only want to run this effect when allAvailableRows or initialExpandedRows changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialExpandedRows]);

    useEffect(() => {
        const prevAvailableRows = prevAllAvailableRowsRef.current;

        // Clean up expanded rows that no longer exist in allAvailableRows
        // setExpandedRows((prev) =>
        //     prev.filter((row) => allAvailableRows.includes(row))
        // );

        // Auto-expand new rows if all current rows are expanded
        if (
            allAvailableRows.length > 1 &&
            prevAvailableRows.length > 0 &&
            !disableAutoExpandNewRows
        ) {
            // Get currently expanded rows that still exist in allAvailableRows
            const validExpandedRows = expandedRows.filter((row) =>
                allAvailableRows.includes(row)
            );

            // Get the rows that were available before
            const previouslyAvailableRows = prevAvailableRows.filter((row) =>
                allAvailableRows.includes(row)
            );

            // Get truly new rows (not in previous available rows)
            const newRows = allAvailableRows.filter(
                (row) => !prevAvailableRows.includes(row)
            );

            // If all previously available rows are expanded and there are new rows, expand the new ones too
            if (
                previouslyAvailableRows.length > 0 &&
                validExpandedRows.length === previouslyAvailableRows.length &&
                newRows.length > 0
            ) {
                setExpandedRows(allAvailableRows);
            }
        }

        // Update the ref for next comparison
        prevAllAvailableRowsRef.current = allAvailableRows;
        // We only want to run this effect when allAvailableRows changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allAvailableRows]);

    // Clean up expanded rows if all available rows are removed
    useEffect(() => {
        if (allAvailableRows.length === 0 && expandedRows.length > 0) {
            collapseAllRows();
        }
    }, [allAvailableRows, collapseAllRows, expandedRows.length]);

    return (
        <ExpandedRowsContext.Provider value={contextValue}>
            {children}
        </ExpandedRowsContext.Provider>
    );
};

export { ExpandedRowsProvider, useExpandedRows };
